import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import annotationPlugin from 'chartjs-plugin-annotation'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip
} from 'chart.js'
import { ConsumptionChartProps, ConsumptionWrapperProps } from './types'
import { MinMaxData } from 'components/charts/types'
import { getConsumptionChartDatasets } from './chartjs.config'
import { calculateMinMaxConsumptionData, getConsumptionEntryColor, getVolumeFractionDigits } from './utils'
import ConsumptionChartDesktop from './ChartDesktop'
import ConsumptionChartMobile from './ChartMobile'
import { useStoreSelector } from 'hooks/store.ts'
import { yScaleTitle } from 'components/charts/plugins.ts'
import { hasSmartMeter } from 'utils/contracts.ts'
import Legend from 'components/charts/legend/Legend.tsx'
import { formatAmount } from 'utils/format.ts'
import { useTranslation } from 'react-i18next'
import { VolumeEntry } from 'pages/app/consumption/your-consumption/components/consumption-section/types.ts'
import { selectCurrentServiceContracts, selectIsProducerAccount } from 'store/contact/slice.ts'
import ChartLayout from 'components/charts/chart-layout/ChartLayout.tsx'

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, LineController, Tooltip, annotationPlugin, yScaleTitle)

const ConsumptionChart = ({
  compact,
  activeEnergyType,
  data: rawData,
  totals,
  isFetchingVolumes,
  granularity,
  hasInjection,
  isTablet,
  showEstimatedUsage,
  layoutClassName
}: ConsumptionWrapperProps) => {
  // Redux
  const isProducer = useStoreSelector(selectIsProducerAccount)
  const { electricity } = useStoreSelector(selectCurrentServiceContracts)

  // i18n
  const { t } = useTranslation(['consumption', 'common'])

  // Local state
  const [activeDataPointIndex, setActiveDataPointIndex] = useState<number | null>(null)

  // Contracts
  const data = isProducer
    ? rawData.map((entry: VolumeEntry): VolumeEntry => ({
        ...entry,
        injection: {
          billed: Math.abs(entry.injection?.billed || 0),
          unbilled: Math.abs(entry.injection?.unbilled || 0)
        }
      }))
    : rawData

  // Constants
  const showConsumption = !isProducer || (isProducer && !!totals.totalConsumption)
  const volumeFormatOptions = { maxFractionDigits: getVolumeFractionDigits(granularity), minFractionDigits: 0 }

  // Memoized constants
  const labels = useMemo<string[]>(() => data.map((consumption) => dayjs(consumption.datetime).toISOString()), [data])
  const minMaxData = useMemo<MinMaxData>(() => calculateMinMaxConsumptionData(data, isProducer), [data, isProducer])

  const chartData = useMemo<ChartData<'bar', (number | null)[]>>(
    () => ({
      labels,
      datasets: getConsumptionChartDatasets({
        labels,
        data,
        type: activeEnergyType,
        showInjection: hasInjection,
        showEstimatedUsage,
        hasSmartMeter: hasSmartMeter(electricity)
      })
    }),
    [labels, data, activeEnergyType, hasInjection, showEstimatedUsage, electricity]
  )

  const chartProps: ConsumptionChartProps = {
    activeEnergyType,
    chartData,
    isFetchingVolumes,
    labels,
    minMaxData,
    granularity,
    setHoverDataPointIndex: setActiveDataPointIndex,
    compact
  }

  /**
   * Get active chart data based on index and key
   * @param {number} index
   * @param {'consumption' | 'injection'} key
   * @returns {number}
   */
  const getActiveChartData = (index: number, key: 'consumption' | 'injection'): number => {
    return (data[index][key]?.billed || 0) + (data[index][key]?.unbilled || 0)
  }

  // Reset active data point index if data is not available
  useEffect(() => {
    if (isFetchingVolumes || (typeof activeDataPointIndex === 'number' && !data[activeDataPointIndex])) {
      setActiveDataPointIndex(null)
    }
  }, [activeDataPointIndex, data, isFetchingVolumes])

  return (
    <ChartLayout className={layoutClassName}>
      {/* CHART */}
      {isTablet ? <ConsumptionChartMobile {...chartProps} /> : <ConsumptionChartDesktop {...chartProps} />}

      {/* LEGEND */}
      <Legend boxed={!compact}>
        {typeof activeDataPointIndex === 'number' && !!data[activeDataPointIndex] ? (
          <>
            {/* Total consumption */}
            {showConsumption && (
              <Legend.Entry
                tooltip={t(($) => $.chart.totals.consumption.tooltip[activeEnergyType])}
                isLoading={isFetchingVolumes}
                label={t(($) => $.chart.totals.consumption.text)}
                indicator={{
                  color: getConsumptionEntryColor(
                    activeEnergyType,
                    data[activeDataPointIndex].consumption.unbilled ? 'unbilled' : 'billed'
                  ),
                  full: true
                }}
                addendum={data[activeDataPointIndex]?.consumption?.unbilled ? t(($) => $.chart.totals.consumption.addendum) : undefined}
              >
                <data value={getActiveChartData(activeDataPointIndex, 'consumption')}>
                  {formatAmount(getActiveChartData(activeDataPointIndex, 'consumption'), volumeFormatOptions)} kWh
                </data>
              </Legend.Entry>
            )}

            {/* Total injection */}
            {!!totals.totalInjection && (
              <Legend.Entry
                tooltip={t(($) => $.chart.totals.injection.tooltip)}
                isLoading={isFetchingVolumes}
                label={t(($) => $.chart.totals.injection.text)}
                indicator={{
                  color: getConsumptionEntryColor('IN', data[activeDataPointIndex].injection?.unbilled ? 'unbilled' : 'billed'),
                  full: true
                }}
                addendum={data[activeDataPointIndex]?.injection?.unbilled ? t(($) => $.chart.totals.injection.addendum) : undefined}
              >
                <data value={Math.abs(getActiveChartData(activeDataPointIndex, 'injection'))}>
                  {formatAmount(Math.abs(getActiveChartData(activeDataPointIndex, 'injection')), volumeFormatOptions)} kWh
                </data>
              </Legend.Entry>
            )}
          </>
        ) : (
          <>
            {/* Total consumption */}
            {showConsumption && (
              <Legend.Entry
                tooltip={t(($) => $.chart.totals.consumption.tooltip[activeEnergyType])}
                isLoading={isFetchingVolumes}
                label={t(($) => $.chart.totals.consumption.text)}
                indicator={[
                  data.some((entry) => !!entry.consumption?.billed) && {
                    color: getConsumptionEntryColor(activeEnergyType, 'billed'),
                    full: true
                  },
                  data.some((entry) => !!entry.consumption?.unbilled) && {
                    color: getConsumptionEntryColor(activeEnergyType, 'unbilled'),
                    full: true
                  }
                ]}
              >
                <data value={totals.totalConsumption}>{formatAmount(totals.totalConsumption, { fractionDigits: 0 })} kWh</data>
              </Legend.Entry>
            )}

            {/* Total injection */}
            {!!totals.totalInjection && (
              <Legend.Entry
                tooltip={t(($) => $.chart.totals.injection.tooltip)}
                isLoading={isFetchingVolumes}
                label={t(($) => $.chart.totals.injection.text)}
                indicator={[
                  data.some((entry) => !!entry.injection?.billed) && { color: getConsumptionEntryColor('IN', 'billed'), full: true },
                  data.some((entry) => !!entry.injection?.unbilled) && { color: getConsumptionEntryColor('IN', 'unbilled'), full: true }
                ]}
              >
                <data value={Math.abs(totals.totalInjection)}>
                  {formatAmount(Math.abs(totals.totalInjection), { fractionDigits: 0 })} kWh
                </data>
              </Legend.Entry>
            )}
          </>
        )}
      </Legend>
    </ChartLayout>
  )
}

export default ConsumptionChart
